import React, { useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { User, Transaction } from '../App';
import { TrendingUp } from 'lucide-react';
import { PaymentModal } from './PaymentModal';
import { NodeDetailsModal } from './NodeDetailsModal';
import { NodeReservationModal } from './NodeReservationModal';
import { MyNodes } from './MyNodes';
import { i18n } from '../utils/i18n';

interface StakingProps {
  user: User;
  onUpdateBalance: (newBalance: number) => void;
  onAddTransaction: (transaction: Transaction) => void;
}

export function Staking({ user, onUpdateBalance, onAddTransaction }: StakingProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState<'products' | 'my-nodes'>('products');
  const [showPayment, setShowPayment] = useState(false);
  const [showNodeDetails, setShowNodeDetails] = useState(false);
  const [showNodeReservation, setShowNodeReservation] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<{
    amount: number;
    currency: 'USDT' | 'VTTN';
    title: string;
    description: string;
    type: 'node' | 'premium' | 'basic';
  } | null>(null);

  const apiBase = useMemo(() => {
    const envValue = (import.meta as any)?.env?.VITE_API_URL?.trim?.();
    if (envValue) {
      return envValue.endsWith('/') ? envValue.slice(0, -1) : envValue;
    }
    if (typeof window === 'undefined') return 'https://wallet.vatton.app';
    try {
      const url = new URL(window.location.origin);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        url.hostname = 'localhost';
        url.port = '3600';
        url.protocol = 'http:';
        return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
      }
      return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
    } catch (error) {
      return 'https://wallet.vatton.app';
    }
  }, []);

  const handleSubscribe = (type: 'node' | 'premium' | 'basic') => {
    let config;
    
    switch (type) {
      case 'node':
        config = {
          amount: 120000,
          currency: 'VTTN' as const,
          title: 'Vatton Full Node Subscription',
          description: 'Subscribe to run a validator node - 12만 VTTN으로 노드 구독 및 보상 받기',
          type: 'node' as const
        };
        break;
      case 'premium':
        config = {
          amount: 5000,
          currency: 'VTTN' as const,
          title: 'Premium Staking',
          description: 'Stake your VTTN tokens and earn 25% APY',
          type: 'premium' as const
        };
        break;
      case 'basic':
        config = {
          amount: 3000,
          currency: 'VTTN' as const,
          title: 'Basic Staking',
          description: 'Start staking with basic plan and earn 18% APY',
          type: 'basic' as const
        };
        break;
    }
    
    setPaymentConfig(config);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async () => {
    if (paymentConfig) {
      try {
        const token = localStorage.getItem('vatton-jwt-token');
        if (!token) {
          console.error('인증 토큰이 없습니다.');
          return;
        }

        // 노드 구독인 경우 예약 API 사용
        if (paymentConfig.type === 'node') {
          const response = await fetch(`${apiBase}/api/nodes/reserve`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              productId: 'validator-node',
              amount: paymentConfig.amount,
              pin: prompt(i18n.t('enterPinPrompt'))
            })
          });

          if (!response.ok) {
            throw new Error(i18n.t('nodeReservationFailed'));
          }
          
          const nodeData = await response.json();
          console.log('✅ 노드 예약 완료:', nodeData);
          alert(i18n.t('nodeReservationSuccess').replace('{id}', nodeData.data.reservationId));
          return;
        }

        // 새로운 스테이킹 API 사용
        const response = await fetch(`${apiBase}/api/staking/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            poolId: paymentConfig.type === 'basic' ? 1 : 2, // Basic: 1, Premium: 2
            amount: paymentConfig.amount,
            pin: prompt(i18n.t('enterPinPrompt'))
          })
        });

        if (!response.ok) {
          throw new Error(i18n.t('stakingSubscribeFailed'));
        }

        const result = await response.json();

        // 트랜잭션 추가
        const transaction = {
          type: paymentConfig.title,
          amount: paymentConfig.currency === 'VTTN' ? -paymentConfig.amount : 0,
          createdAt: new Date().toISOString()
        };
        await onAddTransaction(transaction);

        // VTTN 잔고 업데이트
        if (paymentConfig.currency === 'VTTN') {
          await onUpdateBalance(user.balance - paymentConfig.amount);
        }

        console.log('스테이킹 구독 성공:', result);
      } catch (error) {
        console.error('스테이킹 구독 오류:', error);
      }
    }
    setPaymentConfig(null);
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div>
            <h1 className="text-2xl font-light text-foreground">{i18n.t('staking')}</h1>
            <p className="text-muted-foreground">{i18n.t('earnRewards')}</p>
          </div>
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-border">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'products'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {i18n.t('stakingProducts')}
          </button>
          <button
            onClick={() => setActiveTab('my-nodes')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'my-nodes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {i18n.t('myNodes')}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'my-nodes' ? (
          <MyNodes />
        ) : (
          <>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-500 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-sm mb-1">{i18n.t('availableBalance')}</p>
                <p className="text-3xl font-light">{(user?.balance || 0).toFixed(2)}</p>
                <p className="text-white/60 text-sm">VTTN</p>
              </div>
              <TrendingUp className="h-8 w-8 text-white/60" />
            </div>
          </CardContent>
        </Card>

        {/* Staking Products */}
        <div className="space-y-6">
          {/* Validator Node - Premium Card */}
          <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white border-0 shadow-2xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="bg-white/30 text-white text-xs px-3 py-1 rounded-full font-bold backdrop-blur-sm">
                      PREMIUM
                    </span>
                  </div>
                  <h3 className="font-bold text-xl mb-1">
                    {i18n.t('validatorNode')}
                  </h3>
                  <p className="text-white/90 text-sm">{i18n.t('runValidatorNode')}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">32%</p>
                  <p className="text-white/80 text-sm">APY</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('subscriptionFee')}</p>
                  <p className="font-bold text-lg">60,000</p>
                  <p className="text-white/80 text-xs">USDT</p>
                </div>
                <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('rewards')}</p>
                  <p className="font-bold text-lg">120,000</p>
                  <p className="text-white/80 text-xs">VTTN</p>
                </div>
              </div>

              <div className="bg-white/20 rounded-xl p-4 mb-6 backdrop-blur-sm">
                <div className="flex justify-between items-center">
                  <span className="text-white/90 text-sm">{i18n.t('stakingPeriod')}</span>
                  <span className="font-bold text-xl">12 {i18n.t('months')}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button 
                  onClick={() => setShowNodeDetails(true)}
                  className="bg-white/20 text-white py-4 rounded-xl font-medium border-2 border-white/40 backdrop-blur-sm hover:bg-white/30 transition-all duration-300 text-center shadow-lg"
                >
                  <div className="flex flex-col items-center space-y-1">
                    <span className="text-sm">📋</span>
                    <span>{i18n.t('details')}</span>
                  </div>
                </button>
                <button 
                  onClick={() => setShowNodeReservation(true)}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white py-4 rounded-xl font-bold transition-all duration-300 text-center shadow-xl border-2 border-yellow-400"
                >
                  <div className="flex flex-col items-center space-y-1">
                    <span className="text-sm">🚀</span>
                    <span>{i18n.t('reserveNodeFree')}</span>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Premium Staking */}
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-bold text-xl mb-1">
                    {i18n.t('premiumStaking')}
                  </h3>
                  <p className="text-white/90 text-sm">{i18n.t('higherReturns')}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">25%</p>
                  <p className="text-white/80 text-sm">APY</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('minStake')}</p>
                  <p className="text-white font-bold">5,000</p>
                  <p className="text-white/80 text-xs">VTTN</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('lockPeriod')}</p>
                  <p className="text-white font-bold">6 {i18n.t('months')}</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('expected')}</p>
                  <p className="text-green-300 font-bold">+1,250</p>
                  <p className="text-white/80 text-xs">VTTN</p>
                </div>
              </div>

              <button 
                onClick={() => handleSubscribe('premium')}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg text-center"
              >
                {i18n.t('subscribeNow')}
              </button>
            </CardContent>
          </Card>

          {/* Basic Staking */}
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-white font-bold text-xl mb-1">
                    {i18n.t('basicStaking')}
                  </h3>
                  <p className="text-white/90 text-sm">{i18n.t('safeStableOption')}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">18%</p>
                  <p className="text-white/80 text-sm">APY</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('minStake')}</p>
                  <p className="text-white font-bold">3,000</p>
                  <p className="text-white/80 text-xs">VTTN</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('lockPeriod')}</p>
                  <p className="text-white font-bold">3 {i18n.t('months')}</p>
                </div>
                <div className="bg-white/20 rounded-xl p-3 text-center backdrop-blur-sm">
                  <p className="text-white/80 text-xs mb-1">{i18n.t('expected')}</p>
                  <p className="text-green-300 font-bold">+540</p>
                  <p className="text-white/80 text-xs">VTTN</p>
                </div>
              </div>

              <button 
                onClick={() => handleSubscribe('basic')}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg text-center"
              >
                {i18n.t('subscribeNow')}
              </button>
            </CardContent>
          </Card>
        </div>
        </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentConfig && (
        <PaymentModal
          isOpen={showPayment}
          onClose={() => setShowPayment(false)}
          amount={paymentConfig.amount}
          currency={paymentConfig.currency}
          title={paymentConfig.title}
          description={paymentConfig.description}
          onSuccess={handlePaymentSuccess}
          userBalance={user.balance}
        />
      )}

      {/* Node Details Modal */}
      <NodeDetailsModal
        isOpen={showNodeDetails}
        onClose={() => setShowNodeDetails(false)}
        nodeId="validator-node-001"
      />

      {/* Node Reservation Modal */}
      <NodeReservationModal
        isOpen={showNodeReservation}
        onClose={() => setShowNodeReservation(false)}
        onPayment={() => {
          setShowNodeReservation(false);
          handleSubscribe('node');
        }}
        user={user}
      />
    </div>
  );
}